/** biome-ignore-all lint/style/useNamingConvention: allow camel case */
import {
  type ChangeEvent,
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {v4} from 'uuid';
import type {CreateFlowFeatureControllerToggleProps} from '~/CreateFabricInvitation/types/types';
import {scrollEventDetailsModuleIntoView} from '~/CreateFabricInvitation/utils/misc';
import {
  InvitationWidget,
  type InvitationWidgetOverlayConfig,
} from '~/common/components/InvitationWidget/InvitationWidget';
import {selectEventLinks, updateEventLinks} from '~/common/event/eventAttributes/reducer';
import type {EventLink} from '~/common/event/types';
import {
  formatUrlWithProtocol,
  validURL,
} from '~/common/fabric/CreateDesign/components/EventGifting/utils';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';

import {LinkIcon} from '~library/svg/v2/LinkIcon';
import {PlusIcon} from '~library/svg/v2/PlusIcon';
import {TrashIcon} from '~library/svg/v2/TrashIcon';
import {tanzanite} from '~libSass/v2/_exports.sass';

import './AdditionalLinks.sass';

type DisplayedEventLink = EventLink & {id: string};

const isLinkObjectEmpty = (link: EventLink) =>
  Boolean(!link.url.trim() && !link.display_name?.trim());

interface ControllerContentProps {
  links: DisplayedEventLink[];
  setLinks: Dispatch<SetStateAction<DisplayedEventLink[]>>;
  errors: Record<string, EventLink>;
  setErrors: Dispatch<SetStateAction<Record<string, EventLink>>>;
  isOpen: boolean;
}

const ControllerContent: FC<ControllerContentProps> = ({
  isOpen,
  links,
  setLinks,
  errors,
  setErrors,
}) => {
  const handleAddLink = () => {
    setLinks((prev) => [...prev, {url: '', display_name: '', id: v4()}]);
  };

  const handleRemoveLink = (id: string) => {
    setLinks((prev) => prev.filter((link) => link.id !== id));
  };

  const handleChangeLink = (id: string, field: keyof EventLink, value: string) => {
    setLinks((prev) =>
      prev.map((link) => {
        if (link.id === id) {
          return {...link, [field]: value};
        }
        return link;
      })
    );
    setErrors((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: '',
      },
    }));
  };

  return (
    <>
      {links.map((link, index) => (
        <div className="additional-links__section" key={link.id}>
          <div className="additional-links__section-header">
            <Typography variant="label2" as="label" size="small">
              Link {index + 1}
            </Typography>
            {links.length > 1 && (
              <Button
                variant="unstyled-button"
                size="small"
                data-qa-id={`remove-additional-link-${index}`}
                onClick={() => handleRemoveLink(link.id)}
              >
                <TrashIcon color={tanzanite} ratio={0.5} />
              </Button>
            )}
          </div>
          <Input
            id={`display-name__${link.id}`}
            data-qa-id={`additional-link-display-name-${index}`}
            value={link.display_name || ''}
            placeholder="Enter link name"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChangeLink(link.id, 'display_name', e.target.value)
            }
            autoFocus={isOpen && index === 0}
          />
          <Input
            id={`url__${link.id}`}
            data-qa-id={`additional-link-url-${index}`}
            value={link.url}
            placeholder="Enter URL"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChangeLink(link.id, 'url', e.target.value)
            }
            error={errors[link.id]?.url}
          />
        </div>
      ))}
      {links.length < 3 && (
        <Button
          variant="link"
          size="small"
          onClick={handleAddLink}
          id="add-additional-link"
          data-qa-id="add-additional-link"
          type="button"
          className="invitation-widget__add-button"
        >
          <PlusIcon ratio={0.5} color={tanzanite} />
          Add additional link
        </Button>
      )}
    </>
  );
};

export const AdditionalLinks: FC<CreateFlowFeatureControllerToggleProps> = ({
  overlayOpen,
  onCloseOverlay,
  renderOverlayOnly = false,
  renderAs = 'block',
  hide = false,
}) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const links: EventLink[] = useSelector(selectEventLinks, shallowEqual);
  const [wipLinks, setWipLinks] = useState<DisplayedEventLink[]>(
    links.filter((l) => !isLinkObjectEmpty(l)).map((l) => ({...l, id: v4()}))
  );
  const [linkErrors, setLinkErrors] = useState<Record<string, EventLink>>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset wipLinks when controller is opened/closed
  useEffect(() => {
    const filledLinks = links.filter((link) => !isLinkObjectEmpty(link));
    if (filledLinks.length > 0) setWipLinks(filledLinks.map((l) => ({...l, id: v4()})));
    else setWipLinks([{url: '', display_name: '', id: v4()}]);
  }, [links, isEditing]);

  useEffect(() => {
    if (overlayOpen === undefined) return;
    // If the overlayOpen prop is provided, use it to control the editing state
    setIsEditing(overlayOpen);
    setLinkErrors({});
  }, [overlayOpen]);

  const hasLinks = useMemo(() => links.length > 0, [links.length]);

  const openEditor = useCallback(() => {
    setIsEditing(true);
    if (hasLinks) scrollEventDetailsModuleIntoView();
  }, [hasLinks]);

  const closeEditor = useCallback(() => {
    setIsEditing(false);
    onCloseOverlay?.();
    setLinkErrors({});
    scrollEventDetailsModuleIntoView();
  }, [onCloseOverlay]);

  const handleRemoveSection = useCallback(() => {
    dispatch(updateEventLinks([]));
    closeEditor();
  }, [closeEditor, dispatch]);

  const handleSaveEventLinks = useCallback(() => {
    const newLinks = wipLinks.filter((link) => !isLinkObjectEmpty(link));
    const errors: Record<string, EventLink> = newLinks.reduce(
      (acc, link) => {
        if (!link.url) {
          // URL is required
          if (!(link.id in acc)) acc[link.id] = {url: ''};
          acc[link.id].url = 'URL is required.';
        } else if (!validURL(link.url)) {
          // Check if the URL is properly formatted
          if (!(link.id in acc)) acc[link.id] = {url: ''};
          acc[link.id].url = 'Invalid URL format.';
        }
        return acc;
      },
      {} as Record<string, EventLink>
    );
    setLinkErrors(errors);
    if (Object.keys(errors).length > 0) return;

    dispatch(
      updateEventLinks(newLinks.map((link) => ({...link, url: formatUrlWithProtocol(link.url)})))
    );
    closeEditor();
  }, [closeEditor, dispatch, wipLinks]);

  const overlayConfig: InvitationWidgetOverlayConfig = useMemo(
    () => ({
      title: 'Links',
      subtitle:
        'Share waivers, docs, or other key info. Have a registry? Add it in the next step to make it easier to find.',
      content: (
        <ControllerContent
          isOpen={isEditing}
          links={wipLinks}
          setLinks={setWipLinks}
          errors={linkErrors}
          setErrors={setLinkErrors}
        />
      ),
      qaId: 'additional-links',
      isOpen: isEditing,
      onSubmit: handleSaveEventLinks,
      onClose: closeEditor,
      ctas: [
        {
          ctaText: 'Save',
          ctaQaId: 'save-event-links',
          ctaVariant: 'submit',
          disabled: !hasLinks && wipLinks.every(isLinkObjectEmpty),
        },
        {
          ctaText: 'Remove section',
          onClick: handleRemoveSection,
          ctaQaId: 'remove-event-links',
          ctaVariant: 'remove',
          hide: !hasLinks,
        },
      ],
    }),
    [
      isEditing,
      closeEditor,
      handleSaveEventLinks,
      handleRemoveSection,
      wipLinks,
      linkErrors,
      hasLinks,
    ]
  );

  return (
    <InvitationWidget.Toggle
      filled={hasLinks}
      editable
      title="Links"
      subtitle={hasLinks ? 'Edit your external links.' : 'Add up to 3 external links.'}
      qaId="button_event-links"
      onClick={openEditor}
      renderAs={renderAs}
      hide={hide}
      startAdornment={<LinkIcon ratio={0.75} color={tanzanite} />}
      controllerConfig={overlayConfig}
      renderOverlayOnly={renderOverlayOnly}
    />
  );
};
